'use client';

import { c as _c } from "react/compiler-runtime";
import '@svenskaspelab/nova-core/nextjs/analytics';
import { useAccountBalance } from '@svenskaspelab/nova-core/nextjs/auth';
import { createMutationFn, wagerMutationKeys } from '../../../../lib/entry.js';
import { useMutation } from '../../../../tanstack/entry.js';
import { placeCouponWager } from '../../../actions/coupon/placeCouponWager.js';
import { useRemovePaidActivatedDrawIdFromUrl } from './useRemovePaidActivatedDrawIdFromUrl.js';
const mutationFn = createMutationFn(placeCouponWager);
/**
 * React hook to place a coupon wager and refresh account balance on success.
 *
 * @param root0 - The parameters for placing a coupon wager
 * @param root0.commissionPercent - Optional commission percentage to be applied to the wager, used for retailer wallet case.
 * @param root0.activatedDrawId - Optional activated draw ID, used for retailer wallet case.
 * @param root0.paymentUrls - Optional payment URLs, used for redirecting in payment middlewares.
 * @param root0.marketplaceWallets - wallet info, used for retailer wallet case.
 * @param root0.callbackOnPaymentMpShares - Optional callback function to be called when payment is aborted due to marketplace shares, used for triggering UI updates in such cases.
 * @param root0.callbackOnPaymentDone - Optional callback function to be called after payment is done, used for triggering UI updates after payment regardless of success or failure.
 * @param root0.role - User role, used to determine if the user is a player or retailer.
 * @param root0.trackTransaction - Function to track ecommerce analytics events, used for tracking payment events.
 * @param root0.trackEventFunction - Function to track general analytics events, used for tracking non-ecommerce events related to placing the wager.
 * @returns Mutation result for placing a coupon wager.
 */
export function usePlaceCouponWager(t0) {
  const $ = _c(25);
  const {
    commissionPercent,
    activatedDrawId,
    paymentUrls,
    marketplaceWallets,
    callbackOnPaymentMpShares,
    callbackOnPaymentDone,
    role,
    trackTransaction,
    trackEventFunction
  } = t0;
  const isPlayer = role === "Player";
  const {
    forceRefresh
  } = useAccountBalance();
  const removePaidActivatedDrawId = useRemovePaidActivatedDrawIdFromUrl();
  let t1;
  if ($[0] !== activatedDrawId) {
    t1 = wagerMutationKeys.placeCouponWager(activatedDrawId);
    $[0] = activatedDrawId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== activatedDrawId || $[3] !== commissionPercent || $[4] !== marketplaceWallets || $[5] !== paymentUrls || $[6] !== role || $[7] !== trackEventFunction || $[8] !== trackTransaction) {
    t2 = async props => mutationFn(props, {
      role,
      commissionPercent,
      activatedDrawId,
      paymentUrls,
      marketplaceWallets,
      trackTransaction,
      trackEventFunction
    });
    $[2] = activatedDrawId;
    $[3] = commissionPercent;
    $[4] = marketplaceWallets;
    $[5] = paymentUrls;
    $[6] = role;
    $[7] = trackEventFunction;
    $[8] = trackTransaction;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== callbackOnPaymentDone || $[11] !== callbackOnPaymentMpShares || $[12] !== forceRefresh || $[13] !== isPlayer || $[14] !== marketplaceWallets || $[15] !== removePaidActivatedDrawId) {
    t3 = data => {
      removePaidActivatedDrawId(data.paidActivatedDrawIds);
      if (callbackOnPaymentDone) {
        callbackOnPaymentDone();
      }
      if (isPlayer) {
        forceRefresh();
        if (callbackOnPaymentMpShares && Object.keys(marketplaceWallets).length > 0) {
          callbackOnPaymentMpShares();
        }
      }
    };
    $[10] = callbackOnPaymentDone;
    $[11] = callbackOnPaymentMpShares;
    $[12] = forceRefresh;
    $[13] = isPlayer;
    $[14] = marketplaceWallets;
    $[15] = removePaidActivatedDrawId;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== callbackOnPaymentDone || $[18] !== callbackOnPaymentMpShares) {
    t4 = error => {
      if (callbackOnPaymentDone) {
        callbackOnPaymentDone();
      }
      if (error.cause && error.cause === "PAYMENT_ABORTED_MP_SHARES" && callbackOnPaymentMpShares) {
        callbackOnPaymentMpShares();
      }
    };
    $[17] = callbackOnPaymentDone;
    $[18] = callbackOnPaymentMpShares;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t3 || $[23] !== t4) {
    t5 = {
      mutationKey: t1,
      mutationFn: t2,
      onSuccess: t3,
      onError: t4
    };
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return useMutation(t5);
}